import { useEffect } from "react"
import { Dialog, DialogContent, DialogTitle } from "@/clientWeb/components/ui/vcDialog"
import { Megaphone, Lock } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useBreakoutBroadcastStore } from "@/features/meeting/store/breakout-broadcast.store"
import { BreakoutBroadcastThread } from "./BreakoutBroadcastThread"

/**
 * What a participant sees of the host's broadcasts.
 *
 * Read-only by design, and visibly so. A broadcast is an announcement into
 * every breakout room at once — a reply would go to nobody in particular, and
 * the room's own chat is where a participant answers. So there is no composer
 * here at all rather than one that is disabled: a greyed-out input reads as
 * something that is temporarily unavailable, which invites the reader to keep
 * trying.
 */
export function BreakoutBroadcastViewer({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const t = useTranslation()
  const messages = useBreakoutBroadcastStore((s) => s.messages)
  const myPeerId = useBreakoutBroadcastStore((s) => s.myPeerId)
  const markRead = useBreakoutBroadcastStore((s) => s.markRead)

  // Open means read — including anything that lands while it is open.
  useEffect(() => {
    if (open) markRead()
  }, [open, messages.length, markRead])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidthClass="max-w-[520px]" className="p-0 gap-0 overflow-hidden">
        {/* No close button of our own: DialogContent renders one in the
            corner already, and a second X beside it read as two different
            ways out of the same dialog. */}
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 pr-12 dark:border-border">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-500/15">
              <Megaphone className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">
                {t("controlBar.broadcastMessage")}
              </DialogTitle>
              <p className="text-xs text-slate-500 dark:text-muted-foreground">
                Announcements from the host
              </p>
            </div>
          </div>
        </div>

        <div className="flex h-[400px] min-h-0 flex-col">
          <BreakoutBroadcastThread
            messages={messages}
            emptyHint="Messages the host sends to the breakout rooms will appear here."
            myPeerId={myPeerId}
          />
          <div className="flex items-center justify-center gap-1.5 border-t border-slate-200 px-4 py-2.5 dark:border-border">
            <Lock className="h-3 w-3 text-slate-400 dark:text-muted-foreground" />
            <p className="text-[11px] text-slate-400 dark:text-muted-foreground">
              Only the host can send here — reply in your room's chat.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
